import { useCallback, useEffect, useMemo, useState } from 'react'
import { getReport, historyFor, lastMonths, toCsv, trendFor } from '../lib/reports.js'
import { peso, shortMonth } from '../lib/format.js'

/* Axis ticks carry the month only. At twelve across a phone, "OCT '25" runs
   into its neighbour — the range bar and the summary card already say which
   year this is. */
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const tick = (key) => MONTHS_SHORT[Number(key.slice(5, 7)) - 1]

/**
 * Reports, rebuilt. The old version charted salary against paid, ignored
 * pending, had no month summary, no way to follow one line, and no category
 * totals. Each of those is fixed here.
 *
 * The rule running through the whole screen: every figure appears twice, as
 * paid and as planned. A mid-month report that only counts what has cleared
 * flatters him, and that is the fault he actually felt.
 */

const RANGES = [
  { key: 6, label: '6 months' },
  { key: 12, label: '12 months' },
]

/* Savings rate over time. One series, so no legend — the title names it.
   The running month is drawn hollow because it is not a finished figure. */
function RateChart({ months, selected, onSelect, thisMonth }) {
  const W = 320
  const H = 96
  const PAD_B = 16
  const top = 8
  const max = Math.max(30, ...months.map((m) => Math.max(m.ratePaid, m.ratePlanned)))
  const bw = W / months.length
  const gap = Math.min(6, bw * 0.28)
  const h = (v) => ((H - PAD_B - top) * v) / max

  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label="Savings rate by month">
      <line x1="0" y1={H - PAD_B} x2={W} y2={H - PAD_B} stroke="var(--rule)" strokeWidth="1" />
      {months.map((m, i) => {
        const x = i * bw + gap / 2
        const w = bw - gap
        const running = m.key === thisMonth
        const hp = h(m.ratePaid)
        const on = selected === m.key
        return (
          <g key={m.key} onClick={() => onSelect(on ? null : m.key)} style={{ cursor: 'pointer' }}>
            {/* Full-height hit target, so a thin bar is still tappable. */}
            <rect x={i * bw} y="0" width={bw} height={H} fill="transparent" />
            {running ? (
              <rect x={x} y={H - PAD_B - h(m.ratePlanned)} width={w} height={h(m.ratePlanned)}
                fill="none" stroke="var(--ocean)" strokeWidth="1" strokeDasharray="2 2" rx="2" />
            ) : null}
            <rect x={x} y={H - PAD_B - hp} width={w} height={Math.max(hp, 1)} rx="2"
              fill="var(--ocean)" opacity={on || selected === null ? 1 : 0.35} />
            <text x={i * bw + bw / 2} y={H - 5} textAnchor="middle"
              className="ctick">{tick(m.key)}</text>
          </g>
        )
      })}
    </svg>
  )
}

/* MOB against SWB, stacked. Two series, so a legend is present and both are
   direct-labeled in the readout below. 2px gap between the segments. */
function SplitChart({ months, selected, onSelect }) {
  const W = 320
  const H = 92
  const PAD_B = 16
  const top = 6
  const max = Math.max(1, ...months.map((m) => m.mobPlanned + m.swbPlanned))
  const bw = W / months.length
  const gap = Math.min(6, bw * 0.28)
  const h = (v) => ((H - PAD_B - top) * v) / max

  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label="Spending against wealth building, by month">
      <line x1="0" y1={H - PAD_B} x2={W} y2={H - PAD_B} stroke="var(--rule)" strokeWidth="1" />
      {months.map((m, i) => {
        const x = i * bw + gap / 2
        const w = bw - gap
        const mob = h(m.mobPlanned)
        const swb = h(m.swbPlanned)
        const dim = selected !== null && selected !== m.key ? 0.35 : 1
        return (
          <g key={m.key} onClick={() => onSelect(selected === m.key ? null : m.key)}
            style={{ cursor: 'pointer' }}>
            <rect x={i * bw} y="0" width={bw} height={H} fill="transparent" />
            <rect x={x} y={H - PAD_B - mob} width={w} height={Math.max(mob, 0)} rx="2"
              fill="var(--c-mob)" opacity={dim} />
            {/* 2px surface gap keeps the two fills from reading as one block. */}
            <rect x={x} y={H - PAD_B - mob - swb - 2} width={w} height={Math.max(swb, 0)} rx="2"
              fill="var(--c-swb)" opacity={dim} />
            <text x={i * bw + bw / 2} y={H - 5} textAnchor="middle"
              className="ctick">{tick(m.key)}</text>
          </g>
        )
      })}
    </svg>
  )
}

/** One line across the range. Gaps are real — a month without it is not zero. */
function TrendChart({ points }) {
  const W = 320
  const H = 70
  const vals = points.filter((p) => p.present).map((p) => p.amount)
  if (vals.length < 2) return null
  const lo = Math.min(...vals)
  const hi = Math.max(...vals)
  const span = hi - lo || 1
  const x = (i) => (i / (points.length - 1)) * (W - 8) + 4
  const y = (v) => H - 12 - ((v - lo) / span) * (H - 26)

  /* A month the line was absent breaks the path rather than drawing through
     it — a gap is not a zero. Track the previous point instead of inspecting
     the string, which is how this got written wrong the first time. */
  let d = ''
  let prevPresent = false
  points.forEach((p, i) => {
    if (!p.present) { prevPresent = false; return }
    d += (prevPresent ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(p.amount).toFixed(1) + ' '
    prevPresent = true
  })

  const last = [...points].reverse().find((p) => p.present)
  const lastI = points.lastIndexOf(last)

  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="This line over time">
      <path d={d.trim()} fill="none" stroke="var(--ocean)" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      {points.map((p, i) => p.present ? (
        <circle key={p.key} cx={x(i)} cy={y(p.amount)} r="2.4" fill="var(--ocean)" />
      ) : null)}
      <text x={x(lastI)} y={y(last.amount) - 7} textAnchor="end" className="clabel">
        {peso(last.amount)}
      </text>
    </svg>
  )
}

export default function Reports({ toast }) {
  const [range, setRange] = useState(12)
  const [report, setReport] = useState(null)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState(null)
  const [line, setLine] = useState('')
  const [history, setHistory] = useState([])

  const months = useMemo(() => lastMonths(range), [range])
  const thisMonth = months[months.length - 1]

  const load = useCallback(async () => {
    setError(null)
    try {
      setReport(await getReport(months))
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [months])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!line) { setHistory([]); return }
    historyFor(line).then(setHistory).catch(() => setHistory([]))
  }, [line])

  const shown = selected
    ? report?.byMonth.find((m) => m.key === selected)
    : report?.byMonth[report.byMonth.length - 1]

  const totals = useMemo(() => {
    if (!report) return null
    return report.byMonth.reduce(
      (a, m) => ({
        in: a.in + m.in,
        mobPaid: a.mobPaid + m.mobPaid,
        swbPaid: a.swbPaid + m.swbPaid,
        planned: a.planned + m.planned,
      }),
      { in: 0, mobPaid: 0, swbPaid: 0, planned: 0 },
    )
  }, [report])

  if (error) {
    return (
      <div className="notice warn">
        <p style={{ margin: 0, fontSize: 14 }}>Could not build the report. {error}</p>
      </div>
    )
  }
  if (!report) return <p className="lbl" style={{ padding: '20px 2px' }}>Loading…</p>

  const empty = report.byMonth.every((m) => m.planned === 0 && m.in === 0)
  const avgRate = totals.in > 0 ? Math.round((totals.swbPaid / totals.in) * 100) : 0

  return (
    <>
      <div className="rangebar">
        {RANGES.map((r) => (
          <button key={r.key} type="button" aria-pressed={range === r.key}
            onClick={() => { setRange(r.key); setSelected(null) }}>
            {r.label}
          </button>
        ))}
        <button type="button" className="linkish" style={{ marginLeft: 'auto' }}
          onClick={() => {
            const blob = new Blob([toCsv(report)], { type: 'text/csv' })
            const a = document.createElement('a')
            a.href = URL.createObjectURL(blob)
            a.download = 'report-' + months[0] + '-to-' + thisMonth + '.csv'
            a.click()
            URL.revokeObjectURL(a.href)
          }}>Export CSV</button>
      </div>

      {empty ? (
        <div className="empty">
          <h2 className="serif">Nothing to report yet</h2>
          <p>Once a month or two of payouts and income are in, this fills itself.</p>
        </div>
      ) : null}

      {/* Headline. Savings rate is the number the old reports never showed. */}
      <div className="card">
        <div className="pad">
          <span className="lbl">Savings rate</span>
          <div className="in-total">
            <span className="num serif">{avgRate}%</span>
            <span className="lbl">average over {range} months</span>
          </div>
        </div>
        <div className="chart-wrap">
          <RateChart months={report.byMonth} selected={selected} onSelect={setSelected}
            thisMonth={thisMonth} />
          <p className="lbl chart-note">
            Dashed is {shortMonth(thisMonth)} still running. Tap a month.
          </p>
        </div>
      </div>

      {/* The month summary that does not exist in the current app. */}
      {shown ? (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="pad">
            <span className="lbl">{shortMonth(shown.key)}</span>
            <div className="sum-grid">
              <div><span className="lbl">In</span><b className="num">{peso(shown.in)}</b></div>
              <div><span className="lbl">Rate</span>
                <b className="num">{shown.ratePaid}%<em> / {shown.ratePlanned}% planned</em></b>
              </div>
              <div><span className="lbl">MOB out</span>
                <b className="num mob">{peso(shown.mobPaid)}<em> / {peso(shown.mobPlanned)}</em></b>
              </div>
              <div><span className="lbl">SWB out</span>
                <b className="num swb">{peso(shown.swbPaid)}<em> / {peso(shown.swbPlanned)}</em></b>
              </div>
              <div><span className="lbl">Left after paid</span>
                <b className={'num' + (shown.leftPaid < 0 ? ' neg' : '')}>{peso(shown.leftPaid)}</b>
              </div>
              <div><span className="lbl">Left after all</span>
                <b className={'num' + (shown.leftPlanned < 0 ? ' neg' : '')}>{peso(shown.leftPlanned)}</b>
              </div>
            </div>
            <p className="lbl" style={{ margin: '10px 0 0' }}>
              Left of each pair is paid. Right is everything planned.
            </p>
          </div>
        </div>
      ) : null}

      <p className="lbl" style={{ margin: '22px 2px 8px' }}>Spending against wealth building</p>
      <div className="card">
        <div className="chart-wrap">
          <SplitChart months={report.byMonth} selected={selected} onSelect={setSelected} />
          <div className="split-key" style={{ padding: '2px 15px 0' }}>
            <span><i style={{ background: 'var(--c-mob)' }} />MOB {peso(totals.mobPaid)} paid</span>
            <span><i style={{ background: 'var(--c-swb)' }} />SWB {peso(totals.swbPaid)} paid</span>
          </div>
        </div>
      </div>

      {report.byCategory.length ? (
        <>
          <p className="lbl" style={{ margin: '22px 2px 8px' }}>By category</p>
          <div className="card">
            <div className="pad">
              {report.byCategory.map((c) => {
                const top = report.byCategory[0].planned || 1
                return (
                  <div className="srcrow" key={c.id}>
                    <div className="srchead">
                      <b>{c.name}</b>
                      <span className="num">
                        {peso(c.paid)}<em className="of-planned"> / {peso(c.planned)}</em>
                      </span>
                    </div>
                    <div className="rail">
                      <i style={{
                        width: (c.paid / top) * 100 + '%',
                        background: c.kind === 'build' ? 'var(--c-swb)' : 'var(--c-mob)',
                      }} />
                      <i style={{
                        width: ((c.planned - c.paid) / top) * 100 + '%',
                        background: 'var(--rule)',
                      }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      ) : null}

      {/* Follow one line across months — the grocery bill creeping up. */}
      {report.names.length ? (
        <>
          <p className="lbl" style={{ margin: '22px 2px 8px' }}>Follow one line</p>
          <div className="card">
            <div className="pad">
              <div className="field" style={{ marginBottom: line ? 12 : 0 }}>
                <select value={line} onChange={(e) => setLine(e.target.value)}
                  aria-label="Pick a payable to follow">
                  <option value="">Pick a payable…</option>
                  {report.names.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>

              {line ? (() => {
                const pts = trendFor(report, line)
                const seen = pts.filter((p) => p.present)
                if (seen.length < 2) {
                  return (
                    <p className="lbl" style={{ margin: 0 }}>
                      Only {seen.length === 1 ? 'one month' : 'no months'} of {line} in this range.
                    </p>
                  )
                }
                const first = seen[0]
                const last = seen[seen.length - 1]
                const diff = last.amount - first.amount
                return (
                  <>
                    <TrendChart points={pts} />
                    <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--ink-dim)' }}>
                      {peso(first.amount)} in {shortMonth(first.key)} to {peso(last.amount)} in{' '}
                      {shortMonth(last.key)}
                      {diff === 0 ? ', unchanged' : null}
                    </p>
                    {diff !== 0 ? (
                      <p style={{
                        margin: '3px 0 0', fontSize: 14, fontWeight: 500,
                        color: diff > 0 ? 'var(--late)' : 'var(--health)',
                      }}>
                        {diff > 0 ? '+' : '−'}{peso(Math.abs(diff))} over the range
                      </p>
                    ) : null}
                    {history.length ? (
                      <>
                        <p className="lbl" style={{ margin: '14px 0 4px' }}>When it changed</p>
                        {history.slice(0, 6).map((h) => (
                          <div className="adjrow" key={h.id}>
                            <span className="lbl">
                              {new Date(h.created_at).toLocaleDateString('en-PH',
                                { day: 'numeric', month: 'short' })}
                            </span>
                            <span className="num">
                              {peso(Number(h.old_value))} → {peso(Number(h.new_value))}
                            </span>
                          </div>
                        ))}
                      </>
                    ) : null}
                  </>
                )
              })() : null}
            </div>
          </div>
        </>
      ) : null}
    </>
  )
}
